// import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
// import type { Theme, ThemeContextType } from "@/types";

// const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// export const useTheme = (): ThemeContextType => {
//   const context = useContext(ThemeContext);
//   if (!context) {
//     throw new Error('useTheme must be used within a ThemeProvider');
//   }
//   return context;
// };

// interface ThemeProviderProps {
//   children: ReactNode;
// }

// export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
//   // State should be the theme string itself, not just a boolean
//   const [theme, setTheme] = useState<Theme>(() => {
//     const savedTheme = localStorage.getItem('ricaldai-theme') as Theme | null;
//     if (savedTheme) {
//       return savedTheme;
//     }
//     return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
//   });

//   useEffect(() => {
//     // Logic to apply the theme to the document
//     const root = window.document.documentElement;
//     root.classList.remove('light', 'dark');
//     root.classList.add(theme);
//     localStorage.setItem('ricaldai-theme', theme);
//   }, [theme]);

//   const toggleTheme = () => {
//     setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
//   };

//   // The provided value MUST match the ThemeContextType shape
//   const value: ThemeContextType = {
//     theme: theme,
//     isDark: theme === 'dark', // isDark is now derived from the theme state
//     toggleTheme,
//   };

//   return (
//     <ThemeContext.Provider value={value}>
//       {children}
//     </ThemeContext.Provider>
//   );
// };

// filepath: /Users/Nnamdi2/data_science_projects/B2B_sales_prospecting/prospect-ai-dashboard/src/contexts/ThemeContext.tsx
/* eslint react-refresh/only-export-components: ["error", { "allowConstantExport": true }] */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { clientConfig } from '@/config'

type Theme = 'light' | 'dark'
type Ctx = { theme: Theme; isDark: boolean; toggle: () => void; toggleTheme: () => void }

// Storage key helper with namespace prefix
const getThemeStorageKey = (): string => {
  return `${clientConfig.storage.keyPrefix}${clientConfig.storage.keys.theme}`
}

const ThemeContext = createContext<Ctx | undefined>(undefined)

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Force dark theme for unified luxury aesthetic
  const [theme, setTheme] = useState<Theme>(() => {
    // Check saved preference first, then system preference, default to light
    const saved = localStorage.getItem(getThemeStorageKey()) as Theme | null
    if (saved === 'dark' || saved === 'light') return saved
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark'
    return 'light'
  })

  useEffect(() => {
    const root = document.documentElement
    if (theme === 'dark') {
      root.classList.add('dark')
      root.classList.remove('light')
    } else {
      root.classList.add('light')
      root.classList.remove('dark')
    }
    localStorage.setItem(getThemeStorageKey(), theme)
  }, [theme])

  const toggleFn = () => setTheme(t => (t === 'dark' ? 'light' : 'dark'))
  const value = useMemo<Ctx>(() => ({
    theme,
    isDark: theme === 'dark',
    toggle: toggleFn,
    toggleTheme: toggleFn
  }), [theme])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}